import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import Chart, { ChartData, ChartOptions } from 'chart.js';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ApiText } from '@classes/api-text';
import { Partition } from '@classes/simulation/partition';
import { FormatService } from '@services/format.service';
import { SimulationService } from '@services/simulation.service';
import { CalculatorService } from '@services/calculator.service';

interface GraphTranslations {
    datasets: {
        projectionRates: ApiText;
        projectedCursors: ApiText;
    };
    axes: {
        percents: ApiText;
        years: ApiText;
    };
}

@Component({
    selector: 'app-partition-cursors-chart',
    templateUrl: './partition-cursors-chart.component.html',
    styleUrls: ['./partition-cursors-chart.component.scss']
})
export class PartitionCursorsChartComponent implements OnInit, OnDestroy {
    @Input() partition: Partition;

    private selectedPartSub: Subscription;
    private simulationValSub: Subscription;
    private projRateSub: Subscription;

    chart: Chart;
    chartOptions: ChartOptions;
    chartData: ChartData;

    chartColor = '#c30045';
    chartParamColor = '#d54c7d';

    translations: GraphTranslations;

    constructor(
        private formatSrv: FormatService,
        private simulationSrv: SimulationService,
        private translateSrv: TranslateService,
        private calcSrv: CalculatorService
    ) { }

    ngOnInit() {
        this.initSubscriptions();

        this.translateSrv.get('partitions.projectionGraph.graph')
        .subscribe((res: GraphTranslations) => {
            this.translations = res;
            this.setChart();
        });
    }

    private initSubscriptions(): void {
        this.selectedPartSub = this.simulationSrv.selectedPartitionChanged
        .subscribe(selectedPartition => {
            this.partition = selectedPartition;
            this.setChart();
        });

        this.simulationValSub = this.simulationSrv.simulationValChanged
        .subscribe(simulationValues => {
            this.partition.simulationValues = simulationValues;
            this.setChart();
        });

        this.projRateSub = this.simulationSrv.projectionRateChanged
        .subscribe(projectionRate => {
            this.partition.projectionRate = projectionRate;
            this.setChart();
        });
    }

    // calculate charts values with projection rate and cursors values
    private setChart(): void {
        this.initChart();

        const exexYears = this.partition.simulationValues.years.filter(y => !y.isEstimated);
        const lastExecYear = exexYears[exexYears.length - 1];

        // Init array with last executed year (begin at 0)
        const labelsArray: string[] = [lastExecYear.year.toString()];
        const calcsArray: number[] = [0];
        const naturalProgressArray: number[] = [0];

        const offset = this.partition.cursorParameters.offset;
        const offsetCategory = this.partition.partitionDetails.offsetCategory;
        const projectionRate = this.partition.projectionRate.rate;

        // Store previous values to use and update in loop
        let prevDelta: number = null;
        let prevAmount = lastExecYear.amount || 1;
        let prevNaturalProgress = lastExecYear.amount || 1;

        this.partition.simulationValues.years
        .map((year, i, years) => {
            if(!year.isEstimated) {
                year.indice = 0;
                return year;
            }

            const prevIndice: number = years[i - 1].indice + offset;
            const grandPrevIndice: number = years[i - 2].indice + offset;
            let formattedIndice: number, amountAdd: number, calculatedRate: number, naturalRate: number;

            // Format indice to always have a %
            const calculatedIndice = this.calcSrv.calculateIndice(year.indice, offsetCategory, prevIndice, grandPrevIndice, prevDelta);
            formattedIndice = calculatedIndice.indice;
            prevDelta = calculatedIndice.delta;

            prevAmount = this.calcSrv.calculateAmount(prevAmount, projectionRate, formattedIndice);
            prevNaturalProgress = this.calcSrv.calculateAmount(prevNaturalProgress, projectionRate, 0);
            amountAdd = prevAmount + year.additionalAmount;

            if(lastExecYear.amount) {
                calculatedRate = amountAdd / lastExecYear.amount;
                naturalRate = prevNaturalProgress / lastExecYear.amount;
            } else {
                naturalRate = prevNaturalProgress;
                calculatedRate = prevAmount;

                if(year.additionalAmount) {
                    calculatedRate = prevAmount + 1;
                }
            }

            labelsArray.push(year.year.toString());
            calcsArray.push(this.formatSrv.precision((calculatedRate - 1) * 100, 2));
            naturalProgressArray.push(this.formatSrv.precision((naturalRate - 1) * 100, 2));
        });

        this.chartData.labels = labelsArray;
        this.chartData.datasets[0].data = calcsArray;
        this.chartData.datasets[1].data = naturalProgressArray;

        this.setChartData();
    }

    // create charts data objects
    private initChart(): void {
        this.chartData = {
            labels: [],
            datasets: [{
                label: this.translations.datasets.projectedCursors.shortLabel,
                data: [],
                borderColor: this.chartColor,
                backgroundColor: this.chartColor,
                pointBackgroundColor: this.chartColor,
                borderWidth: 2,
                fill: false,
                lineTension: 0,
            }, {
                label: this.translations.datasets.projectionRates.shortLabel,
                data: [],
                borderColor: this.chartParamColor,
                backgroundColor: "#FFF",
                pointBackgroundColor: this.chartParamColor,
                borderDash: [8, 8],
                borderWidth: 2,
                fill: false,
                lineTension: 0
            }]
        };

        this.chartOptions = {
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: true
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translations.axes.percents.shortLabel
                    }
                }],
                xAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: this.translations.axes.years.shortLabel
                    }
                }]
            }
        };
    }

    private setChartData(): void {
        if(!this.chart) {
            this.chart = new Chart('canvas', {
                type: 'line',
                data: this.chartData,
                options: this.chartOptions
            });
        } else {
            this.chart.data = this.chartData;
            this.chart.update(0);
        }
    }

    ngOnDestroy() {
        if(this.selectedPartSub) {
            this.selectedPartSub.unsubscribe();
        }

        if(this.simulationValSub) {
            this.simulationValSub.unsubscribe();
        }

        if(this.projRateSub) {
            this.projRateSub.unsubscribe();
        }
    }
}
